import { Alert, CircularProgress, Stack } from '@mui/material';
import { Suspense, lazy, type ComponentType } from 'react';
import { useIntl } from 'react-intl';

import { createAuthorizationHeaders } from '../../api/httpClient';
import RemoteBoundary from '../../components/AppLayout/RemoteBoundary';
import { useUserData } from '../../redux/selectors';

interface OnboardingRequestDetails {
  businessJustification: string;
  team?: string;
  manager?: string;
  reference?: string;
  enterpriseName?: string;
  submittedAt?: string;
}

interface OnboardingPendingPageProps {
  request?: OnboardingRequestDetails;
  apiConfig?: {
    authV3Base: string;
    userEmail: string;
    getAuthHeaders?: () => Promise<HeadersInit | undefined>;
  };
  locale?: string;
}

const OnboardingPendingPage = lazy<ComponentType<OnboardingPendingPageProps>>(
  () => import('onboardingMicrofrontend/OnboardingPendingPage')
);

const OnboardingPending = (): React.JSX.Element => {
  const intl = useIntl();
  const user = useUserData();
  const authV3Base = import.meta.env.VITE_AUTH_V3_BASE;
  const requestProps = user.onboardingRequest ? { request: user.onboardingRequest } : {};

  const apiConfig = {
    authV3Base,
    userEmail: user.email,
    getAuthHeaders: createAuthorizationHeaders,
  };

  return (
    <RemoteBoundary
      name='OnboardingPendingPage'
      microfrontend='onboarding-mf'
      fallback={
        <Alert severity='warning'>{intl.formatMessage({ id: 'onboardingUnavailable' })}</Alert>
      }
    >
      <Suspense
        fallback={
          <Stack
            alignItems='center'
            padding={4}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <OnboardingPendingPage
          apiConfig={apiConfig}
          locale={user.locale ?? 'en'}
          {...requestProps}
        />
      </Suspense>
    </RemoteBoundary>
  );
};

export default OnboardingPending;
